import { useEffect, useState } from 'react';

import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import {
    categorySlugToDisplayInput,
    isValidSavedRowCategorySlug,
    resolveSavedRowCategorySlugFromInput,
} from 'utils/savedEmailRows';

import type { SavedRowCategoryOption } from 'utils/savedEmailRows';

export type SaveRowDialogInitialValues = {
    name: string;
    categorySlug: string;
};

/** `create` = new library row; `update` / `saveAsNew` only apply when {@link Props.initialValues} is set. */
export type SaveRowDialogIntent = 'create' | 'update' | 'saveAsNew';

export type Props = {
    open: boolean;
    isSaving: boolean;
    /** When set, dialog is in “update existing library row” mode (prefilled + Update CTA). */
    initialValues?: SaveRowDialogInitialValues | null;
    /** GET /saved-rows/categories in flight */
    categoriesLoading?: boolean;
    /** Category handles from GET /saved-rows/categories only (value = slug). */
    categoryOptions: SavedRowCategoryOption[];
    onClose: () => void;
    onSubmit: (input: { name: string; category: string }, intent: SaveRowDialogIntent) => void | Promise<void>;
};

const SaveRowDialog = ({
    open,
    isSaving,
    initialValues = null,
    categoriesLoading = false,
    categoryOptions,
    onClose,
    onSubmit,
}: Props) => {
    const [name, setName] = useState('');
    const [categoryInput, setCategoryInput] = useState('');
    const isUpdate = Boolean(initialValues);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (initialValues) {
            setName(initialValues.name);
            setCategoryInput(categorySlugToDisplayInput(initialValues.categorySlug, categoryOptions));
        } else {
            setName('');
            setCategoryInput('');
        }
    }, [open, initialValues, categoryOptions]);

    const resolvedCategory = resolveSavedRowCategorySlugFromInput(categoryInput, categoryOptions);
    const categoryValid = isValidSavedRowCategorySlug(resolvedCategory);

    const submitWithIntent = (intent: SaveRowDialogIntent) => {
        const trimmed = name.trim();
        if (!trimmed || trimmed.length < 1 || !categoryValid) {
            return;
        }
        void onSubmit({ name: trimmed, category: resolvedCategory }, intent);
    };

    const handleDialogClose = (_event: unknown, _reason: 'backdropClick' | 'escapeKeyDown') => {
        if (isSaving) {
            return;
        }
        onClose();
    };

    const inputsLocked = isSaving;

    return (
        <Dialog
            disableAutoFocus
            disableEnforceFocus
            disableEscapeKeyDown={isSaving}
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={handleDialogClose}
            // Bee iframe / overlays steal focus and sit above MUI’s default modal z-index; keep our UI usable.
            sx={{ zIndex: 25000 }}
        >
            <DialogTitle>Save Row</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {isUpdate ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            data-testid="save-row-dialog-update-description"
                        >
                            This row has already been saved. You can update its details, or save it as a new library
                            row.
                        </Typography>
                    ) : null}
                    <TextField
                        autoFocus
                        fullWidth
                        label="Row name"
                        value={name}
                        disabled={inputsLocked}
                        inputProps={{ maxLength: 200 }}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <Autocomplete
                        freeSolo
                        fullWidth
                        disabled={inputsLocked}
                        options={categoryOptions}
                        loading={categoriesLoading}
                        loadingText="Loading…"
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
                        inputValue={categoryInput}
                        onInputChange={(_, v) => setCategoryInput(v)}
                        onChange={(_, newValue) => {
                            if (typeof newValue === 'string') {
                                setCategoryInput(newValue);
                            } else if (newValue) {
                                setCategoryInput(newValue.label);
                            }
                        }}
                        noOptionsText="No options"
                        ListboxProps={{
                            sx: {
                                maxHeight: 'min(280px, calc(100vh - 200px))',
                                overflow: 'auto',
                                boxSizing: 'border-box',
                            },
                        }}
                        componentsProps={{
                            popper: {
                                placement: 'bottom-start',
                                sx: { zIndex: 26001 },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Category"
                                error={Boolean(categoryInput.trim()) && !categoryValid}
                            />
                        )}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    px: 3,
                    pb: 2,
                }}
            >
                <Button
                    disabled={inputsLocked}
                    data-testid="save-row-dialog-cancel"
                    onClick={() => {
                        if (!inputsLocked) {
                            onClose();
                        }
                    }}
                >
                    Cancel
                </Button>
                {isUpdate ? (
                    <>
                        <Button
                            disabled={inputsLocked || !name.trim() || !categoryValid}
                            variant="outlined"
                            data-testid="save-row-dialog-save-as-new"
                            onClick={() => submitWithIntent('saveAsNew')}
                        >
                            Save as new
                        </Button>
                        <Button
                            disabled={inputsLocked || !name.trim() || !categoryValid}
                            variant="contained"
                            data-testid="save-row-dialog-primary-action"
                            onClick={() => submitWithIntent('update')}
                        >
                            Update
                        </Button>
                    </>
                ) : (
                    <Button
                        disabled={inputsLocked || !name.trim() || !categoryValid}
                        variant="contained"
                        data-testid="save-row-dialog-primary-action"
                        onClick={() => submitWithIntent('create')}
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SaveRowDialog;
